import { Injectable } from '@angular/core';

import * as _ from 'lodash';
import { ApiHelperService } from '../../core/api-helper.service';

import { ComparisonFeeGroupings } from '../comparison-fee-groupings/comparison-fee-groupings.model';
import { ErrorContext, ServerError, SwipesumError } from '../errors/error.model';
import { BaseFee, BaseFeeGrouping,
    FeeGroupings,
    InterchangeDescriptor,
    InterchangeFee,
    InterchangeFeeGrouping,
    NonNegotiableFee,
    NonNegotiableFeeGrouping,
    ProcessorFee,
    ProcessorFeeGrouping
} from './fee-groupings.model';

class FeeGroupingUploadError extends ServerError {
    constructor(msg: string, ctx?: ErrorContext) {
        super(msg, ctx);

        this.ctx.userHeader =  'Internal Error';
        this.ctx.userMessage = 'We could not synchronize fees. Please contact support.';
    }
}

@Injectable({ providedIn: 'root' })
export class FeeGroupingsService {

    interchangeDescriptorList: InterchangeDescriptor[] = [];
    interchangeProgramList: { id: string, name: string }[] = [];
    options = { authenticate: true, setRoot: true, isPdf: true };

    constructor(
        private api: ApiHelperService,
    ) {
        /*
         * Populate descriptor lists
         */

        this.api.apiCallSane('/interchange/descriptions', 'GET', this.api.getDefaultHeaders(), null, null, this.options)
            .then((data: InterchangeDescriptor[]) =>
                this.interchangeDescriptorList = data)
            .catch((error: Error) => {
                this.api.reportAndRethrow(error);
                this.interchangeDescriptorList = [];
            });

        this.api.apiCallSane('/interchange/program-names',  'GET', this.api.getDefaultHeaders(), null, null, this.options)
            .then((data: { id: string, name: string }[]) => {
                this.interchangeProgramList = data;
            })
            .catch((error: Error) => {
                this.api.reportAndRethrow(error);
                this.interchangeProgramList = [];
            });
    }

    postInterchangeDescriptorList(body: InterchangeDescriptor[]) {
        return this.api.apiCallSane('/interchange/descriptions', 'POST', this.api.getDefaultHeaders(), null, body, this.options);
    }

    syncFeeDescriptions(body: FeeGroupings): Promise<void> {
        return this.api.apiCallSane('/fee-descriptions',
            'POST',
            this.api.getDefaultHeaders(),
            null,
            body,
            this.options)
        .catch(e => {
            throw FeeGroupingUploadError.upgrade(e);
        });
    }

    // TODO (jacob): This is literally just a constructor.
    // why the fuck is this here?
    getProcessorFeeGrouping(procFees): ProcessorFeeGrouping {
        const newProcFees = procFees.map(pf => {
            const { label, totalAmount, totalTransactions, totalVolume, feeDetails } = pf;
            const newFeeDetails = feeDetails.map(fd => {
                fd.transactionRate = parseFloat(fd.transRatePercent) / 100;
                fd.amount = _.round((fd.volume * (fd.transactionRate || 0))
                    + ((fd.transactionFee || 0) * (fd.monthlyTransactions || 0)), 2);
                return fd;
            });
            const newFee = {
                label,
                feeDetails: newFeeDetails,
                // potentially replaced
                totalAmount,
                totalVolume,
                totalTransactions
            };
            const newTotalVol = this.getTotal(newFeeDetails, 'volume');
            if (totalVolume !== newTotalVol && newTotalVol !== 0) {
                newFee.totalVolume = newTotalVol;
            }

            const newTotalTrans = this.getTotal(newFeeDetails, 'monthlyTransactions');
            if (totalTransactions !== newTotalTrans && newTotalTrans !== 0) {
                newFee.totalTransactions = newTotalTrans;
            }

            const newTotalAmt = this.getTotal(newFeeDetails, 'amount');
            if (totalAmount !== newTotalAmt && newTotalAmt !== 0) {
                newFee.totalAmount = newTotalAmt;
            }

            return new ProcessorFee(newFee);
        });

        return new ProcessorFeeGrouping({ fees: newProcFees });
    }

    getAssessmentFeeGrouping(assessFees): BaseFeeGrouping {
        const newAssessFees = assessFees.map(af => {
            return {
                label: af.label,
                amount: af.amount
            };
        });

        return new BaseFeeGrouping({ fees: newAssessFees });
    }

    getNonNegotiableFeeGrouping(nonNegotFees): NonNegotiableFeeGrouping {
        const newNonNegotFees = nonNegotFees.map(nf => {
            return {
                label: nf.label,
                amount: nf.amount,
                isFlagged: nf.isFlagged
            };
        });

        return new NonNegotiableFeeGrouping({ fees: newNonNegotFees });
    }

    getInterchangeFeeGrouping(icFees): InterchangeFeeGrouping {
        const newIcFees = icFees.map(icf => {
            const newFee = {
                label: icf.label,
                amount: icf.amount,
                isFlagged: icf.isFlagged,
                isPadded: icf.isPadded
            };
            if (icf.isPadded && icf.trueAmount) {
                newFee['trueAmount'] = icf.trueAmount;
            }
            return newFee;
        });

        return new InterchangeFeeGrouping({ fees: newIcFees });
    }

    aggregateFeeGroupings(originalFeeGroupings: Array<FeeGroupings>): ComparisonFeeGroupings {
        const processorFees: ProcessorFee[] | undefined = [];
        const additionalFees: BaseFee[] | undefined = [];
        const assessmentFees: BaseFee[] | undefined = [];
        const nonNegotiableFees: NonNegotiableFee[] | undefined = [];
        const interchangeFees: InterchangeFee[] | undefined = [];

        for (const feeGrouping of originalFeeGroupings) {
            // processor fees
            if (feeGrouping.processor.fees && feeGrouping.processor.fees.length) {
                processorFees.push(...feeGrouping.processor.fees);
            }

            // additional fees
            if (feeGrouping.additional.fees && feeGrouping.additional.fees.length) {
                additionalFees.push(...feeGrouping.additional.fees);
            }

            // assessment fees
            if (feeGrouping.assessment.fees && feeGrouping.assessment.fees.length) {
                assessmentFees.push(...feeGrouping.assessment.fees);
            }

            // non-negotiable fees
            if (feeGrouping.nonNegotiable.fees && feeGrouping.nonNegotiable.fees.length) {
                nonNegotiableFees.push(...feeGrouping.nonNegotiable.fees);
            }

            // interchange fees
            if (feeGrouping.interchange.fees && feeGrouping.interchange.fees.length) {
                interchangeFees.push(...feeGrouping.interchange.fees);
            }
        }

        const aggProcFees = this.aggregateProcessorFees(processorFees);
        const aggAdditFees = this.aggregateAdditionalFees(additionalFees);
        const aggAssessFees = this.aggregateAssessmentFees(assessmentFees);
        const aggNonNegotFees = this.aggregateNonNegotiableFees(nonNegotiableFees);
        const aggInterFees = this.aggregateInterchangeFees(interchangeFees);

        return {
            processor: {
                totalVolume: this.getTotal(aggProcFees, 'totalVolume'),
                totalTransactions: this.getTotal(aggProcFees, 'totalTransactions'),
                totalAmount: this.getTotal(aggProcFees, 'totalAmount'),
                proposedTotalAmount: this.getTotal(aggProcFees, 'proposedTotalAmount'),
                fees: aggProcFees
            },
            additional: {
                totalAmount: this.getTotal(aggAdditFees, 'amount'),
                proposedTotalAmount: this.getTotal(aggAdditFees, 'proposedAmount'),
                fees: aggAdditFees
            },
            assessment: {
                totalAmount: this.getTotal(aggAssessFees, 'amount'),
                proposedTotalAmount: this.getTotal(aggAssessFees, 'proposedAmount'),
                fees: aggAssessFees
            },
            nonNegotiable: {
                totalAmount: this.getTotal(aggNonNegotFees, 'amount'),
                proposedTotalAmount: this.getTotal(aggNonNegotFees, 'proposedAmount'),
                fees: aggNonNegotFees
            },
            interchange: {
                totalAmount: this.getTotal(aggInterFees, 'amount'),
                proposedTotalAmount: this.getTotal(aggInterFees, 'proposedAmount'),
                fees: aggInterFees
            }
        };
    }

    // TODO MMD - consolidate these aggregate functions!!
    aggregateProcessorFees(fees) {
        const reducedProcFees = fees.reduce((acc, value) => {
            const { label, totalAmount, totalTransactions, totalVolume, feeDetails } = value;
            if (acc.has(label)) {
                const data = acc.get(label);

                const concatFeeDtls = data.feeDetails.concat(feeDetails);
                const newFeeDetails = this.generateProcProposedFeeDetails(concatFeeDtls);
                const newVolume = _.round(data.totalVolume + totalVolume, 2),
                    newMthlyTrans = _.round(data.totalTransactions + totalTransactions);
                const newTotalAmt = this.getTotal(newFeeDetails, 'amount');
                const newProposedTotalAmt = this.getTotal(newFeeDetails, 'proposedAmount');
                acc.set(label, {
                    totalVolume: newVolume,
                    totalTransactions: newMthlyTrans,
                    totalAmount: newTotalAmt,
                    proposedTotalAmount: newProposedTotalAmt,
                    feeDetails: newFeeDetails
                });
            } else {
                const newFeeDetails = this.generateProcProposedFeeDetails(feeDetails);
                acc.set(label, {
                    totalVolume: totalVolume,
                    totalTransactions: totalTransactions,
                    totalAmount: totalAmount,
                    proposedTotalAmount: totalAmount,
                    feeDetails: newFeeDetails
                });
            }
            return acc;
        }, new Map<string, any>());

        return FeeGroupingsService.transformReducedFees(reducedProcFees);
    }

    aggregateAdditionalFees(fees) {
        const reducedFees = fees.reduce((acc, value) => {
            const { label, amount } = value;
            if (acc.has(label)) {
                const data = acc.get(label);
                const newTotal = _.round(data.amount + amount, 2);
                acc.set(label, {
                    amount: newTotal,
                    proposedAmount: newTotal
                });
            } else {
                acc.set(label, {
                    amount,
                    proposedAmount: amount
                });
            }
            return acc;
        }, new Map<string, any>());

        return FeeGroupingsService.transformReducedFees(reducedFees);
    }

    aggregateAssessmentFees(fees) {
        const reducedFees = fees.reduce((acc, value) => {
            const { label, amount } = value;
            if (acc.has(label)) {
                const data = acc.get(label);
                const newTotal = _.round(data.amount + amount, 2);
                acc.set(label, {
                    amount: newTotal,
                    proposedAmount: newTotal
                });
            } else {
                acc.set(label, {
                    amount,
                    proposedAmount: amount
                });
            }
            return acc;
        }, new Map<string, any>());

        return FeeGroupingsService.transformReducedFees(reducedFees);
    }

    aggregateNonNegotiableFees(fees) {
        const reducedFees = fees.reduce((acc, value) => {
            const { label, amount, isFlagged } = value;
            if (acc.has(label)) {
                const data = acc.get(label);
                const newTotal = _.round(data.amount + amount, 2);
                acc.set(label, {
                    amount: newTotal,
                    proposedAmount: newTotal,
                    isFlagged
                });
            } else {
                acc.set(label, {
                    amount,
                    proposedAmount: amount,
                    isFlagged
                });
            }
            return acc;
        }, new Map<string, any>());

        return FeeGroupingsService.transformReducedFees(reducedFees);
    }

    aggregateInterchangeFees(fees) {
        const reducedFees = fees.reduce((acc, value) => {
            const { label, isFlagged, isPadded, trueAmount } = value;

            let amount = value.amount;
            let proposedAmount = amount;

            if (isPadded && trueAmount != null) {
                proposedAmount = trueAmount;
            }

            if (acc.has(label)) {
                const data = acc.get(label);
                amount  = _.round(data.amount + amount, 2);
                proposedAmount = _.round(data.proposedAmount + proposedAmount, 2);

            }

            acc.set(label, {
                amount, proposedAmount, isFlagged
            });

            return acc;
        }, new Map<string, any>());

        return FeeGroupingsService.transformReducedFees(reducedFees);
    }

    /**
     * Updates the friendly name for a fee grouping
     * @param grouping The fee grouping to update
     * @param friendlyName The new friendly name (can be undefined to clear it)
     */
    updateFeesFriendlyNameGroup(grouping: BaseFeeGrouping, friendlyName: string | undefined): void {
        grouping.friendlyName = friendlyName;
    }

    /**
     * Gets the friendly name for a fee grouping, or returns the default if not set
     * @param grouping The fee grouping to get the friendly name from
     * @param defaultFeeName The default name to return if friendly name is not set
     * @returns The friendly name if available, otherwise the default name
     */
    getStatementFeesFriendlyName(grouping: BaseFeeGrouping, defaultFeeName: string): string | undefined {
        return this.feeGroupingsFriendlyNameNotNull(grouping.friendlyName) 
            ? grouping.friendlyName 
            : defaultFeeName;
    }

    /**
     * Checks if a friendly name is not null, undefined, or empty
     * @param friendlyName The friendly name to check
     * @returns true if the friendly name is valid, false otherwise
     */
    feeGroupingsFriendlyNameNotNull(friendlyName: string | undefined): boolean {
        return friendlyName !== '' && friendlyName !== undefined && friendlyName !== null;
    }

    generateProcProposedFeeDetails(feeDetails) {
        const reducedFeeDetails = feeDetails
            .reduce((fdAcc, fdValue) => {
                const {
                    description,
                    amount,
                    volume,
                    transactionRate,
                    transactionFee,
                    monthlyTransactions
                } = fdValue;

                if (fdAcc.has(description)) {
                    const d = fdAcc.get(description);
                    const newVol = _.round(d.volume + volume, 2);
                    const newMthTrans = _.round(d.monthlyTransactions + monthlyTransactions);

                    const newAmount = _.round((newVol * transactionRate)
                        + ((transactionFee || 0) * (newMthTrans || 0)), 2);

                    const newProposedAmt = _.round((newVol * (d.proposedRate))
                        + ((d.proposedFee || 0) * (newMthTrans || 0)), 2);

                    fdAcc.set(description, {
                        volume: newVol,
                        monthlyTransactions: newMthTrans,
                        transactionRate: d.transactionRate,
                        transactionFee: d.transactionFee,
                        amount: newAmount,
                        proposedRate: d.proposedRate,
                        proposedFee: d.proposedFee,
                        proposedAmount: newProposedAmt
                    });
                } else {
                    fdAcc.set(description, {
                        volume, monthlyTransactions,
                        transactionRate, transactionFee, amount,
                        proposedRate: transactionRate,
                        proposedFee: transactionFee,
                        proposedAmount: amount
                    });
                }
                return fdAcc;
            }, new Map());

        return FeeGroupingsService.transformReducedFeeDetails(reducedFeeDetails);
    }

    private static transformReducedFeeDetails(reducedFeeDetails) {
        return _.sortBy(_.transform(
            Array.from(reducedFeeDetails),
            (result: any, value) => {
                const [description, data]: any = value;
                result.push({
                    description,
                    ...data
                });
            }, []
        ), 'description');
    }

    private static transformReducedFees(reducedFees) {
        return _.sortBy(_.transform(
            Array.from(reducedFees),
            (result: any, value) => {
                const [label, data]: any = value;
                result.push({
                    label,
                    ...data
                });
            }, []
        ), 'label');
    }

    sum(...args) {
        return args.reduce((sum, current) => sum + current, 0);
    }

    // TODO (jacob): I think there's a bug here?
    getTotal(fees, key) {
        return this.sum(...fees.filter(f => !!f[key]).map(f => f[key] || 0));
    }
}

